import * as aq from 'arquero';
import { z } from 'zod';

import { prefixStringify } from '../../builtins/helpers.ts';
import { buildFuzzySearcher } from '../../builtins/string-match.ts';
import type { EvalResult } from '../../env.ts';
import { ShapeError } from '../../errors.ts';
import { type AstInput, astShape, nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { withTable } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { enumCol, type Schema, schemaFromEntries, stringCol } from '../../schema/types.ts';
import { expectMapValue, type Value } from '../../value.ts';
import { requireColumns, resolveDsArg, singleColRefAt } from './helpers.ts';

/** Output column name when the caller omits `:into`. */
const DEFAULT_OUTPUT_COLUMN = 'category';
/** Fuse score cutoff when the caller omits `:threshold` (0 = exact, 1 = anything). */
const DEFAULT_THRESHOLD = 0.4;

const OP_LABEL = '(classify ds :col {:CODE ["kw" …]} :into "category" :threshold 0.4)';

/** Member emitted for text that matches no category. Part of every classify enum domain. */
const UNKNOWN_CATEGORY = 'unknown';

/** A category code (e.g. `FCT`) mapped to the keyword phrases that signal it. */
type CategoryKeywords = Readonly<Record<string, readonly string[]>>;

interface ClassifierSpec {
  /** Category code → the keyword/phrase variants that indicate that category. */
  readonly categories: CategoryKeywords;
  /**
   * Fuse score cutoff in [0, 1] (0 = exact match, 1 = match anything). A
   * keyword only counts as present when its best match in the text scores at
   * or below this threshold.
   */
  readonly threshold: number;
}

/**
 * The enum domain for a set of category codes: the codes in declared order,
 * plus the `unknown` fallback. Single source of truth for "what members does a
 * classify column have", shared by the op's static schema declaration and its
 * per-row fallback value.
 */
const membersFromCodes = (codes: readonly string[]): readonly string[] => [...codes, UNKNOWN_CATEGORY];

/**
 * Compile a classifier over keyword sets using the project's Fuse wrapper. Each
 * category keyword is an approximate-substring PATTERN searched within the input
 * text — typo-tolerant and position-independent (`ignoreLocation`), so it suits
 * free-text comments where a keyword may appear anywhere and be misspelled. A
 * category scores as its single best-matching keyword (lowest Fuse score); the
 * text classifies to the lowest-scoring category across all categories, declared
 * order breaking ties. No keyword under threshold ⇒ unclassified (`null`).
 *
 * `compile` builds the (potentially expensive) per-spec searchers ONCE; the
 * returned classifier then runs cheaply over many texts (e.g. one table column).
 */
const compileClassifier = (spec: ClassifierSpec): ((text: string) => string | null) => {
  // Build one searcher per keyword ONCE, reused across every row. `ignoreLocation`
  // lets a short keyword match anywhere in a paragraph-length comment.
  const compiled = Object.entries(spec.categories).map(([code, keywords]) => ({
    code,
    searchers: keywords.map((keyword) => buildFuzzySearcher(keyword, spec.threshold, { ignoreLocation: true })),
  }));
  return (text) =>
    compiled.reduce<{ code: string; score: number } | undefined>((best, { code, searchers }) => {
      const categoryBest = searchers
        .map((searcher) => searcher.searchIn(text))
        .filter((result) => result.isMatch)
        .reduce<number | undefined>(
          (lo, result) => (lo === undefined || result.score < lo ? result.score : lo),
          undefined,
        );
      // Strict `<` so the FIRST (declared-order) category wins ties.
      if (categoryBest === undefined || (best !== undefined && categoryBest >= best.score)) {
        return best;
      }
      return { code, score: categoryBest };
    }, undefined)?.code ?? null;
};

/** The output column name for a parsed `classify` call. */
const outputColumn = (data: unknown): string => (data as { readonly into?: string }).into ?? DEFAULT_OUTPUT_COLUMN;

/**
 * Classify a free-text column into a typed enum column by fuzzy keyword match.
 *
 * `(classify ds :comment {:FCT ["field capacity" "fct"] :WC ["water cut"]} :into "scenarioType")`
 * adds a `scenarioType` enum column whose value is the category whose keywords
 * best match each row's `:comment` (position-independent Bitap; 0 = exact …
 * 1 = anything), or `"unknown"` when no keyword clears the threshold. The
 * declared enum domain is the category codes plus `"unknown"`. Generic over the
 * categories, so any keyword sets can be passed inline — the iPSO scenario-type
 * vocabulary is just one caller.
 */
export const classify = z
  .object({
    name: z.literal('classify'),
    params: z.tuple([astArg('<dataset>'), astArg('<column>'), astArg('<categories>')]),
    into: z.string().optional(),
    threshold: z.number().min(0).max(1).optional(),
  })
  .describe(
    'Add an enum column classifying :column against keyword sets. (classify ds :comment {:FCT ["field capacity" "fct"] :WC ["water cut"]} :into "scenarioType" :threshold 0.4) — fuzzy keyword match (Bitap, position-independent; 0 = exact … 1 = anything; default 0.4); best-matching category wins, below threshold → "unknown". Output is a typed enum of the category codes + "unknown".',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(-> runs (classify :comment {:FCT ["field capacity" "fct"] :WC ["water cut" "reduction in water handling capacity"]} :into "scenarioType"))',
    },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [dsAst, colAst, catsAst] = data.params as readonly [AstInput, AstInput, AstInput];
      const threshold = (data as { readonly threshold?: number }).threshold ?? DEFAULT_THRESHOLD;
      const into = outputColumn(data);

      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, OP_LABEL, ctx);

      const colShape = astShape(colAst);
      if (colShape.kind !== 'keyword') {
        throw new ShapeError(
          'classify column must be a keyword column ref like `:comment`',
          nodePos(ctx.spans, paramPath(ctx.path, 1)),
        );
      }
      const column = colShape.name;
      requireColumns([column], concrete.schema, () => nodePos(ctx.spans, paramPath(ctx.path, 1)));

      const catsPos = nodePos(ctx.spans, paramPath(ctx.path, 2));
      const { value: catsValue } = await ctx.evalNode(catsAst, ctx.env, paramPath(ctx.path, 2));
      const map = expectMapValue(catsValue, OP_LABEL, catsPos);
      const categories: CategoryKeywords = Object.fromEntries(
        Array.from(map.entries, ([code, patterns]) => {
          if (!Array.isArray(patterns)) {
            throw new ShapeError(`classify category :${code} must be a vector of keyword strings`, catsPos);
          }
          return [code, (patterns as readonly Value[]).map((p) => prefixStringify(p))];
        }),
      );

      const classifyText = compileClassifier({ categories, threshold });

      const classified = withTable(concrete, (t) =>
        t.derive({
          [into]: aq.escape(
            (d: Record<string, unknown>) => classifyText(prefixStringify(d[column])) ?? UNKNOWN_CATEGORY,
          ),
        }),
      );
      return { value: classified, env: ctx.env };
    },
    dataset: {
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
        const catsAst = (data as { readonly params: readonly AstInput[] }).params[2];
        const catsShape = astShape(catsAst);
        // Enum members are the category codes (statically readable from the map
        // literal's keys) plus `unknown`. A non-literal categories arg can't be
        // read statically, so the column degrades to a plain string.
        const outType =
          catsShape.kind === 'map' ? enumCol(membersFromCodes(catsShape.entries.map((e) => e.k))) : stringCol;
        return schemaFromEntries([...ctx.inputSchema, [outputColumn(data), outType] as const]);
      },
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => singleColRefAt(data, basePath, 1),
    },
  });
